import cn from 'classnames';
import {ChangeEvent, FC, useState} from 'react';
import {useAtomValue} from 'jotai';
import type {Bank} from '@rocket-ui/lk-api';
import {useFetchBanks} from '@rocket-ui/lk-api';
import {isAuthorizedAtom} from 'features/auth/model/authAtom';
import {bankFromApi, SelectedBank} from '../../../model';
import {BankIcon} from './BankIcon';
import styles from './BankListPanel.module.css';

type SortMode = 'popular' | 'alphabet';

type Props = {
	selectedBankId: number | null;
	onSelect: (bank: SelectedBank) => void;
	sbpBanksOnly?: boolean;
	className?: string;
};

const SearchIcon = () => (
	<svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" aria-hidden>
		<path
			d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Zm8.1 2.1-3.9-3.9"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
		/>
	</svg>
);

export const BankListPanel: FC<Props> = ({selectedBankId, onSelect, sbpBanksOnly = false, className}) => {
	const [search, setSearch] = useState('');
	const [sortMode, setSortMode] = useState<SortMode>('popular');
	const isAuthorized = useAtomValue(isAuthorizedAtom);

	const {data: banks = [], isLoading} = useFetchBanks(
		{
			sbp_banks: sbpBanksOnly || undefined,
			bank_name: search.trim() || undefined,
			sort_by: sortMode,
		},
		{enabled: isAuthorized},
	);

	const handleSelect = (bank: Bank) => {
		onSelect(bankFromApi(bank));
	};

	return (
		<div className={cn(styles.root, className)}>
			<div className={styles.searchWrap}>
				<SearchIcon />
				<input
					type="search"
					className={styles.searchInput}
					placeholder="Название банка"
					value={search}
					onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
				/>
			</div>

			<div className={styles.sortTabs}>
				<button
					type="button"
					className={cn(styles.sortTab, sortMode === 'popular' && styles.sortTabActive)}
					onClick={() => setSortMode('popular')}
				>
					По популярности
				</button>
				<button
					type="button"
					className={cn(styles.sortTab, sortMode === 'alphabet' && styles.sortTabActive)}
					onClick={() => setSortMode('alphabet')}
				>
					По алфавиту
				</button>
			</div>

			<ul className={styles.panel} role="listbox" aria-label="Список банков">
				{isLoading ? (
					<li className={styles.status}>Загрузка…</li>
				) : banks.length === 0 ? (
					<li className={styles.status}>Ничего не нашли</li>
				) : (
					banks.map((bank) => (
						<li key={bank.id} role="none">
							<button
								type="button"
								role="option"
								aria-selected={selectedBankId === bank.id}
								className={cn(styles.option, selectedBankId === bank.id && styles.optionSelected)}
								onClick={() => handleSelect(bank)}
							>
								<BankIcon bank={bank} className={styles.optionFlag} />
								<span className={styles.optionLabel}>{bank.shortName ?? bank.officialName}</span>
							</button>
						</li>
					))
				)}
			</ul>
		</div>
	);
};
